import { Division, Flex, Spin } from "@ir/ui";
import { Navigate, useParams } from "react-router";
import { AppConfig } from "@src/config/AppConfig";
import { MasterDataDeductionCategoriesDataProvider } from "@src/presentation/providers/MasterDataDeductionCategoriesDataProvider";
import { MasterDataDeductionTypesDataProvider } from "@src/presentation/providers/MasterDataDeductionTypesDataProvider";
import { MasterDataReasonCodeMappingsDataProvider } from "@src/presentation/providers/MasterDataReasonCodeMappingsDataProvider";
import { MasterDataDistributionCentersDataProvider } from "@src/presentation/providers/MasterDataDistributionCentersDataProvider";
import { MasterDataProductCategoriesDataProvider } from "@src/presentation/providers/MasterDataProductCategoriesDataProvider";
import { MasterDataProductUomsDataProvider } from "@src/presentation/providers/MasterDataProductUomsDataProvider";
import { MasterDataProductPricesDataProvider } from "@src/presentation/providers/MasterDataProductPricesDataProvider";
import { MasterDataProductsDataProvider } from "@src/presentation/providers/MasterDataProductsDataProvider";
import { MasterDataPurchasersDataProvider } from "@src/presentation/providers/MasterDataPurchasersDataProvider";
import { MasterDataRetailersDataProvider } from "@src/presentation/providers/MasterDataRetailersDataProvider";
import { useMasterDataBrand } from "../MasterDataBrandContext";
import { MasterDataPlaceholderPage } from "../MasterDataPlaceholderPage";
import { MasterDataDistributionCentersFooter } from "../utils/MasterDataDistributionCentersFooter";
import { MasterDataDeductionTypesFooter } from "../utils/MasterDataDeductionTypesFooter";
import { MasterDataReasonCodeMappingsFooter } from "../utils/MasterDataReasonCodeMappingsFooter";
import { MasterDataProductCategoriesFooter } from "../utils/MasterDataProductCategoriesFooter";
import { MasterDataProductPricesFooter } from "../utils/MasterDataProductPricesFooter";
import { MasterDataProductsFooter } from "../utils/MasterDataProductsFooter";
import { MasterDataRetailersFooter } from "../utils/MasterDataRetailersFooter";
import { MasterDataDeductionCategoriesPage } from "./MasterDataDeductionCategoriesPage";
import {
  DEDUCTION_TYPES_PAGE_SIZE,
  MasterDataDeductionTypesPage,
} from "./MasterDataDeductionTypesPage";
import { MasterDataReasonCodeMappingsPage } from "./MasterDataReasonCodeMappingsPage";
import { MasterDataDistributionCentersPage } from "./MasterDataDistributionCentersPage";
import { MasterDataProductCategoriesPage } from "./MasterDataProductCategoriesPage";
import { MasterDataProductPricesPage } from "./MasterDataProductPricesPage";
import { MasterDataProductsPage } from "./MasterDataProductsPage";
import { MasterDataProductUomsPage } from "./MasterDataProductUomsPage";
import { MasterDataPurchasersPage } from "./MasterDataPurchasersPage";
import { MasterDataRetailersPage } from "./MasterDataRetailersPage";
import { isImplementedDeductionSetupSection } from "./deductionSetupSections";
import { isProductCatalogSection } from "./productCatalogSections";
import { isRetailersSection } from "./retailersSections";
import { isTradingPartnersSection } from "./tradingPartnersSections";

export const MasterDataSectionRouter = () => {
  const { organizationKey, brandKey: routeBrandKey, section } = useParams<{
    organizationKey?: string;
    brandKey?: string;
    section?: string;
  }>();
  const { brand, isBrandSelected, isLookupLoading, isLookupReady } =
    useMasterDataBrand();

  if (
    section === "reason-code-mapping" &&
    organizationKey &&
    routeBrandKey
  ) {
    return (
      <Navigate
        to={`/master-data/${organizationKey}/${routeBrandKey}/reason-code`}
        replace
      />
    );
  }

  const isImplementedListSection =
    isTradingPartnersSection(section) ||
    isProductCatalogSection(section) ||
    isRetailersSection(section) ||
    isImplementedDeductionSetupSection(section);

  if (!isImplementedListSection) {
    return <MasterDataPlaceholderPage />;
  }

  if (isLookupLoading || !isLookupReady || !isBrandSelected || !brand) {
    return (
      <Division className="master-data-section">
        <Flex align="center" justifyContent="center" className="py-6">
          <Spin tip="Loading brand..." spinning />
        </Flex>
      </Division>
    );
  }

  const defaultPageSize = AppConfig.getDefaultPaginationLimit();
  const brandKey = brand.brandKey;

  if (section === "purchasers") {
    return (
      <MasterDataPurchasersDataProvider
        key={`purchasers-${brandKey}`}
        brandKey={brandKey}
      >
        <Division className="master-data-section-shell">
          <MasterDataPurchasersPage />
        </Division>
      </MasterDataPurchasersDataProvider>
    );
  }

  if (section === "retailers") {
    return (
      <MasterDataRetailersDataProvider
        key={`retailers-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={defaultPageSize}
      >
        <Division className="master-data-section-shell">
          <MasterDataRetailersPage />
          <MasterDataRetailersFooter />
        </Division>
      </MasterDataRetailersDataProvider>
    );
  }

  if (section === "purchaser-distribution-centers") {
    return (
      <MasterDataDistributionCentersDataProvider
        key={`distribution-centers-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={defaultPageSize}
      >
        <Division className="master-data-section-shell">
          <MasterDataDistributionCentersPage />
          <MasterDataDistributionCentersFooter />
        </Division>
      </MasterDataDistributionCentersDataProvider>
    );
  }

  if (section === "products") {
    return (
      <MasterDataProductsDataProvider
        key={`products-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={defaultPageSize}
      >
        <Division className="master-data-section-shell">
          <MasterDataProductsPage />
          <MasterDataProductsFooter />
        </Division>
      </MasterDataProductsDataProvider>
    );
  }

  if (section === "product-uom") {
    return (
      <MasterDataProductUomsDataProvider
        key={`product-uom-${brandKey}`}
        brandKey={brandKey}
      >
        <Division className="master-data-section-shell">
          <MasterDataProductUomsPage />
        </Division>
      </MasterDataProductUomsDataProvider>
    );
  }

  if (section === "product-categories") {
    return (
      <MasterDataProductCategoriesDataProvider
        key={`product-categories-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={defaultPageSize}
      >
        <Division className="master-data-section-shell">
          <MasterDataProductCategoriesPage />
          <MasterDataProductCategoriesFooter />
        </Division>
      </MasterDataProductCategoriesDataProvider>
    );
  }

  if (section === "deduction-categories") {
    return (
      <MasterDataDeductionCategoriesDataProvider
        key={`deduction-categories-${brandKey}`}
        brandKey={brandKey}
      >
        <Division className="master-data-section-shell">
          <MasterDataDeductionCategoriesPage />
        </Division>
      </MasterDataDeductionCategoriesDataProvider>
    );
  }

  if (section === "types-subtypes") {
    return (
      <MasterDataDeductionTypesDataProvider
        key={`types-subtypes-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={DEDUCTION_TYPES_PAGE_SIZE}
      >
        <Division className="master-data-section-shell">
          <MasterDataDeductionTypesPage />
          <MasterDataDeductionTypesFooter />
        </Division>
      </MasterDataDeductionTypesDataProvider>
    );
  }

  if (section === "reason-code") {
    return (
      <MasterDataReasonCodeMappingsDataProvider
        key={`reason-code-${brandKey}`}
        brandKey={brandKey}
        defaultPageSize={defaultPageSize}
      >
        <Division className="master-data-section-shell">
          <MasterDataReasonCodeMappingsPage />
          <MasterDataReasonCodeMappingsFooter />
        </Division>
      </MasterDataReasonCodeMappingsDataProvider>
    );
  }

  return (
    <MasterDataProductPricesDataProvider
      key={`product-prices-${brandKey}`}
      brandKey={brandKey}
      defaultPageSize={defaultPageSize}
    >
      <Division className="master-data-section-shell">
        <MasterDataProductPricesPage />
        <MasterDataProductPricesFooter />
      </Division>
    </MasterDataProductPricesDataProvider>
  );
};
